import { useEffect, useState } from 'react';

// ── Store URLs — update these when the app is published ──────────────────────
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.sadguruflower';
export const APP_STORE_URL  = 'https://apps.apple.com/in/app/sadguru-flower/id6787900390';
const FALLBACK_URL          = 'https://sadguruflower.com';
const APP_URL               = 'https://sadguruflower.com/app'; // QR code points here
// ─────────────────────────────────────────────────────────────────────────────

function detectPlatform() {
  const ua = navigator.userAgent || '';
  if (/android/i.test(ua))          return 'android';
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  return 'other';
}

function setPageMeta() {
  document.title = 'Download Sadguru Flower App';
  const setMeta = (name, content, prop = false) => {
    const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
    let tag = document.head.querySelector(sel);
    if (!tag) {
      tag = document.createElement('meta');
      prop ? tag.setAttribute('property', name) : tag.setAttribute('name', name);
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', content);
  };
  setMeta('description', 'Download the Sadguru Flower app — order fresh flowers, varmalas, and gifts with same-day delivery across Gujarat.');
  setMeta('robots', 'noindex, follow');
  setMeta('og:title',       'Download Sadguru Flower App', true);
  setMeta('og:description', 'Order farm-fresh flowers and varmalas with same-day delivery. Available on Android and iOS.', true);
  setMeta('og:image',       '/og-image.jpg', true);
  setMeta('apple-itunes-app', 'app-id=6787900390');
  setMeta('google-play-app',  'app-id=com.sadguruflower.app');
}

// Inject spinner animation once
if (!document.getElementById('app-redirect-styles')) {
  const tag = document.createElement('style');
  tag.id = 'app-redirect-styles';
  tag.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(tag);
}

function AppRedirect() {
  const [status, setStatus]     = useState('redirecting'); // 'redirecting' | 'error'
  const [platform, setPlatform] = useState(null);

  useEffect(() => {
    setPageMeta();
    const detected = detectPlatform();
    setPlatform(detected);

    const urlMap = {
      android: PLAY_STORE_URL,
      ios:     APP_STORE_URL,
      other:   FALLBACK_URL,
    };

    const timer = setTimeout(() => {
      try {
        window.location.href = urlMap[detected];
      } catch {
        setStatus('error');
      }
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  const isDesktop    = platform === 'other';
  const isAndroid    = platform === 'android';
  const isIOS        = platform === 'ios';
  const directUrl    = isAndroid ? PLAY_STORE_URL : isIOS ? APP_STORE_URL : FALLBACK_URL;
  const storeLabel   = isAndroid ? 'Google Play Store' : isIOS ? 'Apple App Store' : 'sadguruflower.com';

  return (
    <div style={s.page}>
      <div style={s.card}>

        {/* Logo */}
        <img src="/logo.png" alt="Sadguru Flower" style={s.logo} />

        {/* Status message */}
        {status === 'redirecting' ? (
          <div style={s.statusBlock}>
            <div style={s.spinner} />
            <h1 style={s.heading}>Redirecting you…</h1>
            <p style={s.sub}>Opening <strong>{storeLabel}</strong></p>
            <p style={s.hint}>
              If nothing happens,{' '}
              <a href={directUrl} style={s.link}>tap here</a>.
            </p>
          </div>
        ) : (
          <div style={s.statusBlock}>
            <h1 style={s.heading}>Something went wrong</h1>
            <p style={s.sub}>Use the links below to download the app.</p>
          </div>
        )}

        {/* QR Code — shown on all screens, most useful on desktop */}
        <div style={s.qrWrapper}>
          <p style={s.qrLabel}>
            {isDesktop ? 'Scan with your phone to download' : 'Or scan this QR code'}
          </p>
          <div style={s.qrBox}>
            <img src="/sadguruQrCode.png" alt="Scan to download Sadguru Flower app" style={{width:160, height:160, display:'block'}} />
          </div>
          <p style={s.qrUrl}>sadguruflower.com/app</p>
        </div>

        {/* Store buttons */}
        <div style={s.buttons}>
          <a href={APP_STORE_URL} style={s.storeBtn}>
            <span style={s.storeBtnIcon}>🍎</span>
            <div>
              <div style={s.storeBtnSub}>Download on the</div>
              <div style={s.storeBtnMain}>App Store</div>
            </div>
          </a>
          <a href={PLAY_STORE_URL} style={s.storeBtn}>
            <span style={s.storeBtnIcon}>▶</span>
            <div>
              <div style={s.storeBtnSub}>Get it on</div>
              <div style={s.storeBtnMain}>Google Play</div>
            </div>
          </a>
        </div>

        <a href={FALLBACK_URL} style={s.homeLink}>← Back to website</a>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #fff8f3 0%, #ffeedd 100%)',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    padding: '24px',
  },
  card: {
    background: '#fff',
    borderRadius: '24px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.10)',
    padding: '40px 36px',
    maxWidth: '400px',
    width: '100%',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  },
  logo: {
    height: '68px',
    objectFit: 'contain',
  },
  statusBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #f0e0d0',
    borderTop: '3px solid #f47e20',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    marginBottom: '4px',
  },
  heading: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#1a1a1a',
    margin: 0,
  },
  sub: {
    fontSize: '14px',
    color: '#6b5f5a',
    margin: 0,
  },
  hint: {
    fontSize: '13px',
    color: '#a09590',
    margin: 0,
  },
  link: {
    color: '#f47e20',
    textDecoration: 'underline',
  },
  qrWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
  },
  qrLabel: {
    fontSize: '13px',
    color: '#6b5f5a',
    margin: 0,
  },
  qrBox: {
    padding: '16px',
    background: '#fff',
    borderRadius: '16px',
    border: '2px solid #f0e0d0',
    display: 'inline-flex',
  },
  qrUrl: {
    fontSize: '12px',
    color: '#a09590',
    margin: 0,
    letterSpacing: '0.3px',
  },
  buttons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    width: '100%',
  },
  storeBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    background: '#1a1a1a',
    color: '#fff',
    padding: '13px 20px',
    borderRadius: '14px',
    textDecoration: 'none',
  },
  storeBtnIcon: {
    fontSize: '22px',
    lineHeight: 1,
  },
  storeBtnSub: {
    fontSize: '11px',
    opacity: 0.65,
    textAlign: 'left',
  },
  storeBtnMain: {
    fontSize: '15px',
    fontWeight: 600,
    marginTop: '1px',
    textAlign: 'left',
  },
  homeLink: {
    fontSize: '13px',
    color: '#a09590',
    textDecoration: 'none',
  },
};

export default AppRedirect;
